import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Zap, Globe, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";

const mockEvents = [
  {
    id: 1,
    company: "SpaceX",
    mission: "Falcon 9 Starlink Flight 10",
    status: "launching",
    countdown: 45,
    launchSite: "Kennedy Space Center LC-39A",
    weather: "Optimal",
    payloadCount: 60,
    nextMilestone: "Engine ignition",
    milestones: [
      { name: "Hold", completed: false, time: "T-00:15" },
      { name: "Engine Ignition", completed: false, time: "T-00:03" },
      { name: "Liftoff", completed: false, time: "T+00:00" },
      { name: "Max-Q", completed: false, time: "T+01:00" },
      { name: "MECO", completed: false, time: "T+02:30" },
      { name: "Stage Separation", completed: false, time: "T+02:40" },
    ],
  },
  {
    id: 2,
    company: "Tesla",
    mission: "Powerwall Supply Chain Update",
    status: "scheduled",
    countdown: 3600 * 2,
    launchSite: "Gigafactory Nevada",
    weather: "N/A",
    payloadCount: 0,
    nextMilestone: "Q&A Begins",
    milestones: [
      { name: "Opening Remarks", completed: false, time: "T+00:00" },
      { name: "Product Demo", completed: false, time: "T+15:00" },
      { name: "Q&A Session", completed: false, time: "T+30:00" },
    ],
  },
];

export default function LiveCenter() {
  const [events, setEvents] = useState(mockEvents);
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const [countdowns, setCountdowns] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    setCountdowns(events.reduce((acc, e) => ({ ...acc, [e.id]: e.countdown }), {}));
    const interval = setInterval(() => {
      setCountdowns(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          if (updated[parseInt(key)] > 0) {
            updated[parseInt(key)]--;
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [events]);

  const formatCountdown = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${secs}s`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="space-y-8">
      <motion.div
        className="space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {events.map((event) => (
          <motion.div
            key={event.id}
            variants={itemVariants}
            onClick={() => setSelectedEvent(selectedEvent === event.id ? null : event.id)}
            className={`border p-8 transition-all cursor-pointer ${
              selectedEvent === event.id
                ? "border-foreground bg-card"
                : "border-border hover:border-foreground/50"
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-medium text-foreground">
                    {event.mission}
                  </h2>
                  <Badge
                    variant={
                      event.status === "launching"
                        ? "destructive"
                        : event.status === "scheduled"
                        ? "secondary"
                        : "outline"
                    }
                    className={event.status === "launching" ? "animate-pulse" : ""}
                  >
                    {event.status === "launching"
                      ? "🔴 LIVE"
                      : "Scheduled"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {event.company} • {event.launchSite}
                </p>
              </div>
            </div>

            {/* Countdown (Large) */}
            <motion.div
              className="text-center mb-8 py-6 border border-border bg-background/50"
              animate={{ scale: event.status === "launching" ? [1, 1.02, 1] : 1 }}
              transition={{ duration: 1, repeat: event.status === "launching" ? Infinity : 0 }}
            >
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Time to {event.company === "Tesla" ? "Start" : "Launch"}
              </p>
              <p className="text-5xl font-bold text-foreground font-mono">
                {formatCountdown(countdowns[event.id] || event.countdown)}
              </p>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-border">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Weather</p>
                <p className="text-lg font-medium text-foreground">
                  {event.weather}
                </p>
              </div>
              {event.payloadCount > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payload</p>
                  <p className="text-lg font-medium text-foreground">
                    {event.payloadCount} Satellites
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Next Milestone</p>
                <p className="text-lg font-medium text-foreground">
                  {event.nextMilestone}
                </p>
              </div>
            </div>

            {/* Milestones Timeline */}
            {selectedEvent === event.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mb-6 space-y-3"
              >
                <h3 className="text-sm font-medium uppercase tracking-widest mb-4">
                  Event Timeline
                </h3>
                {event.milestones.map((milestone, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`h-3 w-3 rounded-full border-2 ${
                        milestone.completed
                          ? "bg-foreground border-foreground"
                          : "border-border"
                      }`} />
                      <span className="text-xs font-mono text-muted-foreground min-w-12">
                        {milestone.time}
                      </span>
                    </div>
                    <p className="text-sm text-foreground">{milestone.name}</p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button className="flex-1 bg-foreground text-background hover:bg-foreground/90">
                <Play className="h-4 w-4 mr-2" />
                Watch Stream
              </Button>
              {event.status === "launching" && (
                <Button variant="outline" className="flex-1 border-border animate-pulse">
                  <Zap className="h-4 w-4 mr-2" />
                  Live Analytics
                </Button>
              )}
              <Button variant="outline" className="border-border">
                <AlertCircle className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}
